import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { getTutorStudents } from "./getTutorStudents";
import { getDiagnosticEvents } from "./getDiagnosticEvents";
import { filterTestStudentsForTutor } from "../../utils/tutor/filterTestStudents";

/* ---------- helpers ---------- */

function formatRelativeTime(date) {
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

function getDiagnosticSubject(diagnosticId) {
  if (!diagnosticId) return null;
  if (diagnosticId.includes("english")) return "english";
  if (diagnosticId.includes("math")) return "math";
  return null;
}

/* ---------- main ---------- */

export async function getTutorDashboardUpdates({ tutorUid }) {
  const rawStudents = await getTutorStudents();
  const students = filterTestStudentsForTutor({
    tutorUid,
    students: rawStudents,
  });

  const updates = [];

  for (const student of students) {
    /* ================= ASSIGNMENTS ================= */

    const assignmentQ = query(
      collection(db, "users", student.id, "assignments"),
      orderBy("lastSavedAt", "desc"),
      limit(1)
    );

    const assignmentSnap = await getDocs(assignmentQ);

    if (!assignmentSnap.empty) {
      const d = assignmentSnap.docs[0].data();
      const sortTime = d.lastSavedAt?.toDate?.();

      if (sortTime) {
        updates.push({
          student: student.firstName,
          action:
            d.status === "completed"
              ? `completed ${d.packetName}`
              : d.status === "in_progress"
              ? `started ${d.packetName}`
              : `assigned ${d.packetName}`,
          time: formatRelativeTime(sortTime),
          score:
            d.status === "completed" &&
            d.correct != null &&
            d.questionCount != null
              ? `${d.correct} / ${d.questionCount}`
              : "—",
          sortTime,
        });
      }
    }

    /* ================= DIAGNOSTICS ================= */

    const diagnosticEvents = await getDiagnosticEvents({
      userId: student.id,
      firstName: student.firstName,
    });

    for (const e of diagnosticEvents) {
      updates.push({
        student: e.student,
        action: e.action,
        time: formatRelativeTime(e.sortTime),
        score: e.score,
        sortTime: e.sortTime,
      });
    }

    /* ================= SIGNUP ================= */

    if (student.createdAt) {
      const createdAt = student.createdAt.toDate?.();
      if (createdAt) {
        updates.push({
          student: student.firstName,
          action: "joined Office Hours",
          time: formatRelativeTime(createdAt),
          score: "—",
          sortTime: createdAt,
        });
      }
    }
  }

  /* ================= SORT + LIMIT ================= */

  return updates
    .sort((a, b) => b.sortTime - a.sortTime)
    .slice(0, 15)
    .map(({ sortTime, ...rest }) => rest);
}
